/*
 * Copyright (c) 2023. Collabro Limited
 */

import axios from "axios";
import packageInfo from "../../package.json";
import { UserDetails } from "../interface/credentials";
import { PaletteMode } from "@mui/material";
import { BookingRequest, DisplayController, RosterLayer } from "RosterData";
import { Location } from "SearchParameters";
import { FilterProps } from "../components/Filter/FilterInput";
import { SortProps } from "../components/Sort/SortScreen";
import { PersonnelProfile } from "../components/PersonnelScreen/PersonnelScreen";

export const baseURL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:8080/api"
    : `${packageInfo.homepage.substring(
        packageInfo.homepage.lastIndexOf("/")
      )}/api`;

const API = axios.create({
  baseURL: baseURL,
});

export const getReportData = (
  location: string,
  toDate: string,
  searchQuery: string | null
) =>
  API.get("/mpc", {
    withCredentials: true,
    params: {
      location,
      toDate,
      searchQuery,
    },
  }).then((res) => res.data);

export const getPersonnelProfile = (personnelId: number) =>
  API.get("/personnel", {
    withCredentials: true,
    params: {
      personnelId,
    },
  })
    .then((res) => res.data as PersonnelProfile)
    .catch((err) => console.log(err));

export const getLocations = () =>
  API.get("/location", { withCredentials: true }).then((res) => res.data);

export const getAllLocations = () =>
  API.get("/location/all", { withCredentials: true }).then((res) => res.data);

export const searchPersonnel = (searchQuery: string) =>
  API.get("/personnel/search", {
    withCredentials: true,
    params: { searchQuery },
  }).then((res) => res.data);

export const checkDuplicateBookings = (bookingRequest: BookingRequest) =>
  API.post("/bookings/check", bookingRequest, { withCredentials: true }).then(
    (res) => res.data
  );

export const authenticate = (user: UserDetails) =>
  API.post("/auth/login", user, { withCredentials: true }).then(
    (res) => res.data
  );

export const checkAuthStatus = () =>
  API.get("/auth/status", {
    withCredentials: true,
  }).then((res) => res.data);

export const saveTheme = (theme: { theme: PaletteMode }) =>
  API.post("/auth/preferences/theme", theme, { withCredentials: true });

export const savePreferences = (preferences: DisplayController) =>
  API.post("/auth/preferences/prefs", preferences, { withCredentials: true });

export const saveSort = (sort: SortProps[]) =>
  API.post("/auth/preferences/sort", sort, { withCredentials: true });

export const saveFilter = (filters: FilterProps[]) =>
  API.post("/auth/preferences/filter", filters, { withCredentials: true });

export const saveLocation = (location: Location) =>
  API.post("/auth/preferences/location", location, { withCredentials: true });

export const saveLayers = (layers: string[]) =>
  API.post("/auth/preferences/layers", layers, { withCredentials: true });
export const getPreferences = () =>
  API.get("/auth/preferences", { withCredentials: true }).then(
    (res) => res.data
  );

export const getActiveLayers = (): Promise<RosterLayer[]> =>
  API.get("/layers", { withCredentials: true }).then((res) => res.data);

export const singleBookingRequest = (singleBooking: BookingRequest) =>
  API.post("/bookings/single", singleBooking, { withCredentials: true });
export const logoutUser = () =>
  API.get("/auth/logout", {
    withCredentials: true,
  }).then((res) => res.data);
